import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './HowItWorksDemo.css';

/**
 * Animated demo showing how to use Fantomize:
 * Step 1: Click the button
 * Step 2: Upload/capture image
 * Step 3: Get style results
 */
const HowItWorksDemo = () => {
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    {
      id: 'click',
      title: 'Tap to Start',
      description: 'Click the Fantomize button',
      icon: 'button'
    },
    {
      id: 'upload',
      title: 'Capture Style',
      description: 'Take a photo or upload an image',
      icon: 'camera'
    },
    {
      id: 'results',
      title: 'Get Results',
      description: 'Find matching styles instantly',
      icon: 'results'
    }
  ];

  // Auto-cycle through steps
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStep((prev) => (prev + 1) % steps.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [steps.length]);

  return (
    <div className="how-it-works-demo">
      {/* Phone mockup container */}
      <div className="demo-phone">
        <div className="demo-phone-notch"></div>
        <div className="demo-phone-screen">
          <AnimatePresence mode="wait">
            {/* Step 1: Click Button */}
            {currentStep === 0 && (
              <motion.div
                key="step-click"
                className="demo-step demo-step-click"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.4 }}
              >
                <div className="demo-header">
                  <span className="demo-logo">fantomize</span>
                </div>
                <div className="demo-content-center">
                  <motion.div
                    className="demo-button-wrapper"
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    <div className="demo-fantomize-btn">
                      <svg width="40" height="40" viewBox="0 0 100 100" fill="none" stroke="white" strokeWidth="3">
                        <polygon points="50,5 93,27.5 93,72.5 50,95 7,72.5 7,27.5" />
                        <line x1="50" y1="5" x2="50" y2="50" />
                        <line x1="93" y1="27.5" x2="50" y2="50" />
                        <line x1="93" y1="72.5" x2="50" y2="50" />
                        <line x1="50" y1="95" x2="50" y2="50" />
                        <line x1="7" y1="72.5" x2="50" y2="50" />
                        <line x1="7" y1="27.5" x2="50" y2="50" />
                      </svg>
                    </div>
                    {/* Tap indicator */}
                    <motion.div
                      className="demo-tap-indicator"
                      animate={{
                        scale: [0.8, 1, 0.8],
                        opacity: [0.5, 1, 0.5]
                      }}
                      transition={{ duration: 1, repeat: Infinity }}
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                        <path d="M9 11.24V7.5C9 6.12 10.12 5 11.5 5S14 6.12 14 7.5v3.74c1.21-.81 2-2.18 2-3.74C16 5.01 13.99 3 11.5 3S7 5.01 7 7.5c0 1.56.79 2.93 2 3.74zm9.84 4.63l-4.54-2.26c-.17-.07-.35-.11-.54-.11H13v-6c0-.83-.67-1.5-1.5-1.5S10 6.67 10 7.5v10.74l-3.43-.72c-.08-.01-.15-.03-.24-.03-.31 0-.59.13-.79.33l-.79.8 4.94 4.94c.27.27.65.44 1.06.44h6.79c.75 0 1.33-.55 1.44-1.28l.75-5.27c.01-.07.02-.14.02-.2 0-.62-.38-1.16-.91-1.38z"/>
                      </svg>
                    </motion.div>
                  </motion.div>
                  <p className="demo-instruction">Tap to search</p>
                </div>
              </motion.div>
            )}

            {/* Step 2: Upload/Camera */}
            {currentStep === 1 && (
              <motion.div
                key="step-upload"
                className="demo-step demo-step-upload"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.4 }}
              >
                <div className="demo-upload-zone">
                  <motion.div
                    className="demo-upload-icon"
                    animate={{ y: [0, -8, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                      <circle cx="8.5" cy="8.5" r="1.5"/>
                      <polyline points="21,15 16,10 5,21"/>
                    </svg>
                  </motion.div>
                  <p className="demo-upload-text">Upload or capture</p>
                  <div className="demo-upload-options">
                    <motion.div
                      className="demo-option demo-option-camera"
                      animate={{ scale: [1, 1.05, 1] }}
                      transition={{ duration: 2, repeat: Infinity, delay: 0 }}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 15.2c1.77 0 3.2-1.43 3.2-3.2S13.77 8.8 12 8.8 8.8 10.23 8.8 12s1.43 3.2 3.2 3.2zM9 2L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2H9zm3 15c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z"/>
                      </svg>
                      <span>Camera</span>
                    </motion.div>
                    <motion.div
                      className="demo-option demo-option-gallery"
                      animate={{ scale: [1, 1.05, 1] }}
                      transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M22 16V4c0-1.1-.9-2-2-2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2zm-11-4l2.03 2.71L16 11l4 5H8l3-4zM2 6v14c0 1.1.9 2 2 2h14v-2H4V6H2z"/>
                      </svg>
                      <span>Gallery</span>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 3: Results */}
            {currentStep === 2 && (
              <motion.div
                key="step-results"
                className="demo-step demo-step-results"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.4 }}
              >
                <div className="demo-results-header">
                  <span>Style Matches</span>
                  <span className="demo-results-count">12 found</span>
                </div>
                <div className="demo-results-grid">
                  {[1, 2, 3, 4].map((item, index) => (
                    <motion.div
                      key={item}
                      className="demo-result-card"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.15 }}
                    >
                      <div className="demo-result-image">
                        <div className="demo-result-placeholder"></div>
                        <span className="demo-match-badge">{95 - index * 3}%</span>
                      </div>
                      <div className="demo-result-info">
                        <span className="demo-result-brand">Brand</span>
                        <span className="demo-result-price">$99</span>
                      </div>
                    </motion.div>
                  ))}
                </div>
                <motion.div
                  className="demo-shop-btn"
                  animate={{ scale: [1, 1.02, 1] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  Shop Now
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Step indicators */}
      <div className="demo-steps-indicator">
        {steps.map((step, index) => (
          <div
            key={step.id}
            className={`demo-step-dot ${currentStep === index ? 'active' : ''}`}
            onClick={() => setCurrentStep(index)}
          >
            <div className="demo-step-number">{index + 1}</div>
            <div className="demo-step-info">
              <span className="demo-step-title">{step.title}</span>
              <span className="demo-step-desc">{step.description}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default HowItWorksDemo;


